import { useEffect, useState } from 'react'

/**
 * "12s ago" for a timestamp, on a clock shared by the whole app.
 *
 * There can be a hundred of these mounted at once (one per row, sometimes two).
 * Three things make that affordable:
 *
 *  1. ONE module-level timer. Every hook instance registers a callback in a
 *     shared Set; the timer exists only while at least one subscriber does.
 *     A hundred timers firing independently would be a hundred wakeups a
 *     second and a measurable battery cost on an always-open dashboard.
 *  2. Re-render only when the *rendered string* changes. The tick runs on a
 *     schedule, but a row showing "4m ago" recomputes the same string many
 *     times before it becomes "5m ago" — those are dropped here rather than
 *     turned into React work.
 *  3. **The tick rate follows the data.** Only the first minute of a
 *     timestamp's life is rendered at one-second resolution ("just now",
 *     "37s ago"); after that the label changes once a minute at most. So the
 *     clock runs at 1s only while at least one subscriber is still inside that
 *     first minute, and drops to 15s otherwise — a table full of hour-old
 *     heartbeats costs four wakeups a minute instead of sixty.
 *
 * The 15s ceiling is deliberate rather than "one minute": the customer table
 * re-derives live -> stale off this same tick, so the slow bucket still bounds
 * how long a dead heartbeat can go unnoticed. In practice any screen with a
 * genuinely live session keeps the clock in the 1s bucket anyway.
 */

/** Returns whether this subscriber still needs one-second resolution. */
type Listener = () => boolean

const FAST_MS = 1_000
const SLOW_MS = 15_000

const listeners = new Set<Listener>()
let timer: ReturnType<typeof setInterval> | null = null
/** Current interval period, or 0 when the clock is stopped. */
let periodMs = 0

function stop(): void {
  if (timer !== null) clearInterval(timer)
  timer = null
  periodMs = 0
}

/** Idempotent: re-requesting the period the clock already runs at is a no-op,
 *  so a burst of mounts during a scroll cannot keep restarting the interval. */
function schedule(ms: number): void {
  if (timer !== null && periodMs === ms) return
  if (timer !== null) clearInterval(timer)
  periodMs = ms
  timer = setInterval(tick, ms)
}

function tick(): void {
  let wantsFast = false
  // Copy: a listener may unsubscribe during iteration.
  for (const fn of [...listeners]) {
    if (fn()) wantsFast = true
  }
  if (listeners.size === 0) {
    stop()
    return
  }
  schedule(wantsFast ? FAST_MS : SLOW_MS)
}

function subscribe(listener: Listener, wantsFast: boolean): () => void {
  listeners.add(listener)
  // A fresh subscriber pulls the whole clock up to 1s; anything else joins at
  // whatever rate is already running (SLOW when the clock was stopped).
  schedule(wantsFast ? FAST_MS : periodMs === 0 ? SLOW_MS : periodMs)

  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) stop()
  }
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

function format(iso: string | null | undefined, now: number): string {
  if (!iso) return '—'
  const then = Date.parse(iso)
  if (!Number.isFinite(then)) return '—'

  // Clamp future timestamps: a worker whose clock runs a little fast should
  // read "just now", not "-3s ago".
  const diff = Math.max(0, now - then)

  if (diff < 5_000) return 'just now'
  if (diff < MINUTE) return `${Math.floor(diff / 1000)}s ago`
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m ago`
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`
  return `${Math.floor(diff / DAY)}d ago`
}

/**
 * True while this timestamp's label is still in the per-second bucket. An
 * absent or unparseable timestamp renders a constant and never needs a tick.
 */
function needsFastTick(iso: string | null | undefined, now: number): boolean {
  if (!iso) return false
  const then = Date.parse(iso)
  if (!Number.isFinite(then)) return false
  return now - then < MINUTE
}

/* ------------------------------------------------------------------ *
 * Live elapsed
 * ------------------------------------------------------------------ */

/** `42m 23s`, counting up. Seconds are always shown — this is a stopwatch. */
function elapsedLabel(sinceMs: number, now: number): string {
  const diff = Math.max(0, now - sinceMs)
  const s = Math.floor(diff / 1000)
  if (s < 60) return `${s}s`

  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`

  const h = Math.floor(m / 60)
  return `${h}h ${String(m % 60).padStart(2, '0')}m`
}

/**
 * A stopwatch on the SAME shared clock as `useRelativeTime`.
 *
 * The run console shows "how long has this been running" and it has to move,
 * every second, for as long as the drawer is open — a session time frozen at
 * whatever it was when the last event arrived is the thing that makes a live
 * view feel dead.
 *
 * Reusing the module clock rather than adding a `setInterval` is the whole
 * point: this can be mounted alongside a hundred `useRelativeTime` rows, and one
 * more independent timer per instance is exactly the cost that hook was built to
 * avoid. It requests the fast bucket unconditionally while running, because
 * unlike a relative label a stopwatch changes its rendered string every second
 * forever.
 *
 * Pass `null` to stop the clock — a finished run should hold its final time
 * rather than counting up indefinitely.
 */
export function useLiveElapsed(sinceMs: number | null, frozenMs?: number | null): string {
  const [label, setLabel] = useState(() =>
    sinceMs === null ? '—' : elapsedLabel(sinceMs, frozenMs ?? Date.now())
  )

  useEffect(() => {
    if (sinceMs === null) {
      setLabel('—')
      return
    }

    // A run that has ended renders its final duration once and subscribes to
    // nothing: there is no reason to hold the app's clock in its fast bucket for
    // a number that will never change again.
    if (frozenMs !== null && frozenMs !== undefined) {
      setLabel(elapsedLabel(sinceMs, frozenMs))
      return
    }

    let current = elapsedLabel(sinceMs, Date.now())
    setLabel((prev) => (prev === current ? prev : current))

    return subscribe(() => {
      const next = elapsedLabel(sinceMs, Date.now())
      if (next !== current) {
        current = next
        setLabel(next)
      }
      return true
    }, true)
  }, [sinceMs, frozenMs])

  return label
}

export function useRelativeTime(iso: string | null | undefined): string {
  const [label, setLabel] = useState(() => format(iso, Date.now()))

  useEffect(() => {
    const start = Date.now()
    let current = format(iso, start)
    setLabel((prev) => (prev === current ? prev : current))

    return subscribe(() => {
      const now = Date.now()
      const next = format(iso, now)
      if (next !== current) {
        current = next
        setLabel(next)
      }
      return needsFastTick(iso, now)
    }, needsFastTick(iso, start))
  }, [iso])

  return label
}

/* ------------------------------------------------------------------ *
 * Countdown
 * ------------------------------------------------------------------ */

/** Whole seconds left, rounded UP so it reads 1 until the moment is here. */
function secondsUntil(dueAtMs: number | null, now: number): number | null {
  return dueAtMs === null ? null : Math.max(0, Math.ceil((dueAtMs - now) / 1000))
}

/**
 * Seconds until `dueAtMs`, counting down on the same shared clock.
 *
 * For "Next beat in 45s". The due time is re-anchored by each poll, so this
 * only carries the number between answers — which is the difference between a
 * countdown and a number that moves in 700ms lurches. At 0 it stops ticking
 * and holds until a new due time arrives: past the moment, there is nothing
 * left to count and no reason to keep the clock fast.
 *
 * Pass `null` for no countdown at all.
 */
export function useCountdown(dueAtMs: number | null): number | null {
  const [left, setLeft] = useState(() => secondsUntil(dueAtMs, Date.now()))

  useEffect(() => {
    let current = secondsUntil(dueAtMs, Date.now())
    setLeft((prev) => (prev === current ? prev : current))
    if (current === null || current === 0) return

    return subscribe(() => {
      const next = secondsUntil(dueAtMs, Date.now())
      if (next !== current) {
        current = next
        setLeft(next)
      }
      return next !== null && next > 0
    }, true)
  }, [dueAtMs])

  return left
}
